import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  getInventoryStock, 
  getLowStockProducts,
  getInventoryTransactions,
  recordInventoryTransaction,
  getDailyStockCounts,
  recordStockCount,
  recordBulkStockCounts,
  getDailyDamages,
  recordDamage,
  recordBulkDamages,
  updateReorderLevel,
  recordInboundWithBatchLayer,
  getBatchLayerStatus,
  consumeFromBatchLayers,
  calculateDamageValue
} from '@/services/inventoryService';
import type { 
  CreateInventoryTransactionInput,
  CreateDailyStockCountInput,
  CreateDailyDamageInput
} from '@/types/database/inventory';
import { toast } from 'sonner';

// ==================== Inventory Stock Hooks ====================

export function useInventoryStock(filters?: {
  productId?: string;
  lowStockOnly?: boolean;
  categoryType?: 'Fresh' | 'FMCG' | 'Other';
}) {
  return useQuery({
    queryKey: ['inventory-stock', filters],
    queryFn: () => getInventoryStock(filters),
  });
}

export function useLowStockProducts() {
  return useQuery({
    queryKey: ['low-stock-products'],
    queryFn: getLowStockProducts,
  });
}

export function useUpdateReorderLevel() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ productId, reorderLevel }: { productId: string; reorderLevel: number }) =>
      updateReorderLevel(productId, reorderLevel),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory-stock'] });
      queryClient.invalidateQueries({ queryKey: ['low-stock-products'] });
      toast.success('Reorder level updated');
    },
    onError: (error) => {
      toast.error('Failed to update reorder level: ' + error.message);
    },
  });
}

// ==================== Inventory Transaction Hooks ====================

export function useInventoryTransactions(filters?: {
  productId?: string;
  transactionType?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['inventory-transactions', filters],
    queryFn: () => getInventoryTransactions(filters),
  });
}

export function useRecordTransaction() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (input: CreateInventoryTransactionInput) =>
      recordInventoryTransaction(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory-stock'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['low-stock-products'] });
      toast.success('Transaction recorded');
    },
    onError: (error) => {
      toast.error('Failed to record transaction: ' + error.message);
    },
  });
}

// ==================== Daily Stock Count Hooks ====================

export function useDailyStockCounts(filters?: {
  productId?: string;
  startDate?: string;
  endDate?: string;
}) {
  return useQuery({
    queryKey: ['daily-stock-counts', filters],
    queryFn: () => getDailyStockCounts(filters),
  });
}

export function useRecordStockCount() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (input: CreateDailyStockCountInput) =>
      recordStockCount(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory-stock'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['daily-stock-counts'] });
      toast.success('Stock count recorded');
    },
    onError: (error) => {
      toast.error('Failed to record stock count: ' + error.message);
    },
  });
}

export function useRecordBulkStockCounts() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (counts: CreateDailyStockCountInput[]) =>
      recordBulkStockCounts(counts),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['inventory-stock'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['daily-stock-counts'] });
      toast.success(`${data.length} stock count${data.length !== 1 ? 's' : ''} recorded`);
    },
    onError: (error) => {
      toast.error('Failed to record stock counts: ' + error.message);
    },
  });
}

// ==================== Daily Damage Hooks ====================

export function useDailyDamages(filters?: {
  productId?: string;
  startDate?: string;
  endDate?: string;
  damageType?: string;
}) {
  return useQuery({
    queryKey: ['daily-damages', filters],
    queryFn: () => getDailyDamages(filters),
    refetchOnMount: 'always',
  });
}

export function useRecordDamage() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (input: CreateDailyDamageInput) =>
      recordDamage(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory-stock'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['daily-damages'], exact: false });
      toast.success('Damage recorded');
    },
    onError: (error) => {
      toast.error('Failed to record damage: ' + error.message);
    },
  });
}

export function useRecordBulkDamages() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (damages: CreateDailyDamageInput[]) =>
      recordBulkDamages(damages),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['inventory-stock'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['daily-damages'], exact: false });
      toast.success(`${data.length} damage${data.length !== 1 ? 's' : ''} recorded`);
    },
    onError: (error) => {
      toast.error('Failed to record damages: ' + error.message);
    },
  });
}

// ==================== Batch Layer Hooks ====================

export function useBatchLayerStatus(productId?: string) {
  return useQuery({
    queryKey: ['batch-layer-status', productId],
    queryFn: () => getBatchLayerStatus(productId),
  });
}

export function useConsumeFromBatchLayers() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({
      productId,
      quantity,
      consumptionType,
      referenceId,
      referenceDate,
      notes
    }: {
      productId: string;
      quantity: number;
      consumptionType: 'chipchip_sale' | 'b2b_delivery' | 'damage' | 'adjustment' | 'other';
      referenceId?: string;
      referenceDate?: string;
      notes?: string;
    }) => consumeFromBatchLayers(productId, quantity, consumptionType, referenceId, referenceDate, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batch-layer-status'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-stock'] });
    },
    onError: (error) => {
      toast.error('Failed to consume from batch layers: ' + error.message);
    },
  });
}

export function useCalculateDamageValue() {
  return useMutation({
    mutationFn: ({ productId, quantity }: { productId: string; quantity: number }) =>
      calculateDamageValue(productId, quantity),
  });
}
